import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  dataTestId?: string;
  /** `center` (default) — a centered dialog. `right` — a full-height panel
   *  docked to the right edge (the History overlay shape). */
  placement?: 'center' | 'right';
  /** `default` — the standard centered width. `wide` — a roomier centered
   *  dialog (diff view, version body) that still caps to the viewport. Only
   *  meaningful for `placement="center"`. */
  size?: 'default' | 'wide';
}

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  dataTestId,
  placement = 'center',
  size = 'default'
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const titleId = useRef(
    `pn-modal-title-${Math.random().toString(36).slice(2, 9)}`
  ).current;

  const close = useCallback(() => onClose(), [onClose]);

  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;

    const node = dialogRef.current;
    if (node) {
      const first = node.querySelector<HTMLElement>(FOCUSABLE);
      (first ?? node).focus();
    }

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close();
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusables = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)
      ).filter((el) => !el.hasAttribute('disabled'));
      if (focusables.length === 0) {
        e.preventDefault();
        dialogRef.current.focus();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && (active === first || !dialogRef.current.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      const prev = previouslyFocused.current;
      if (prev && typeof prev.focus === 'function') {
        prev.focus();
      }
    };
  }, [open, close]);

  if (!open) return null;

  const tid = dataTestId ?? 'modal';
  return createPortal(
    <div
      className="pn-modal-backdrop"
      data-placement={placement}
      data-testid={`${tid}-backdrop`}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          e.preventDefault();
          close();
        }
      }}
    >
      <div
        ref={dialogRef}
        className="pn-modal"
        data-placement={placement}
        data-size={size}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        data-testid={tid}
      >
        <header className="pn-modal-head">
          <h2 id={titleId} className="pn-modal-title">
            {title}
          </h2>
          <button
            type="button"
            className="pn-modal-close"
            aria-label="Close"
            data-testid={`${tid}-close`}
            onClick={close}
          >
            ×
          </button>
        </header>
        <div className="pn-modal-body">{children}</div>
        {footer ? <footer className="pn-modal-foot">{footer}</footer> : null}
      </div>
    </div>,
    document.body
  );
}
